<template>
  <vs-wrapper v-if="canShowJobs">
    <div v-if="noJobs" class="no-jobs">
      No jobs found at this location.<br />Try expanding your filters.
    </div>
    <field-view-job-card
      v-for="job in jobs"
      :key="job.id"
      :job="job"
      :link-to="{
        name: 'fvnext-ticket-info-panel-direct',
        params: {
          id: `${job.number}`,
        },
      }"
    />
    <div class="mt-2">
      <job-search-pagination
        v-if="hasNextPage || getCurrentPage > 1"
        :query="jobsQuery"
        :current-page="getCurrentPage"
        :update-page="updatePage"
      />
    </div>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRef, watch } from 'vue'
import { usePagination } from '@/components/jobs/pagination'
import { useJobsQuery } from '@/components/jobs/query'
import { useJobsSearch } from '@/components/jobs/search'
import FieldViewJobCard from './FieldViewJobCard.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useMapFilters } from '@/components/jobs/filters'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'

const props = defineProps<{
  locationId: string | number | null
}>()

const locationIdRef = toRef(props, 'locationId')

const locationsFilter = computed(() => (locationIdRef.value ? [locationIdRef.value] : []))
const filters = useMapFilters({ locations: locationsFilter })
const { getCurrentPage, getItemsPerPage, updatePage } = usePagination()

watch(filters.fingerprint!, () => updatePage(1))

const jobsQuery = reactive(
  useJobsQuery(
    { ...filters, locations: locationsFilter },
    getCurrentPage,
    getItemsPerPage,
    ref(['requested_start', 'number']), // sortBy
    ref([true, true]), // sortDesc
  ),
)

const {
  jobs,
  hasNextPage,
  isFetching: isFetchingJobs,
  isFetched: isFetchedJobs,
  isRefetching: isRefetchingJobs,
} = useJobsSearch(jobsQuery)

const canShowJobs = computed(() => {
  if (isFetchingJobs.value && !isRefetchingJobs.value) {
    return false
  }
  return true
})

const noJobs = computed(() => {
  return isFetchedJobs.value && !isFetchingJobs.value && jobs.value.length === 0
})
</script>

<style scoped>
.no-jobs {
  color: var(--color-grey);
  text-align: center;
  padding: var(--space-large) 0px;
}
</style>
